import React, { useState, useEffect } from 'react'; // Add useEffect
import ContactList from '../components/chat/ContactList';
import ChatArea from '../components/chat/ChatArea';
import { useChat } from '../hooks/useChat';
import { emitEvent, getSocket, subscribeToEvent, unsubscribeFromEvent } from '../lib/websocketService';

const ChatPage: React.FC = () => {
  const [quotedMsgId, setQuotedMsgId] = useState<string | null>(null);
  const {
    contacts,
    messages,
    activeContact,
    activeContactId, // useChat now returns activeContactId directly
    searchTerm,
    selectContact,
    sendMessage,
    updateSearchTerm,
    // Contact pagination
    fetchNextPageContacts,
    hasNextPageContacts,
    isFetchingNextPageContacts,
    isLoadingContacts,
    // Message pagination
    fetchNextPageMessages,
    hasNextPageMessages,
    isFetchingNextPageMessages,
    isLoadingMessages,
  } = useChat();

  useEffect(() => {
    const socket = getSocket();
    let currentChannelId: string | number | null = null;

    // Ensure socket is connected before trying to join a room.
    // The socket connection might be asynchronous.
    const handleConnect = () => {
      if (activeContact && activeContact.channelId) {
        currentChannelId = activeContact.channelId;
        emitEvent('join_channel_room', { channelId: currentChannelId });
      }
    };

    if (activeContact && activeContact.channelId) {
      currentChannelId = activeContact.channelId;
      if (socket && socket.connected) {
        emitEvent('join_channel_room', { channelId: currentChannelId });
      } else if (socket) {
        // If socket exists but not connected, listen for connect event then join
        socket.once('connect', handleConnect);
      }
    }

    const confirmJoinCallback = (data: { room: string }) => {
      console.log('Successfully joined room (WS confirmation):', data.room);
    };
    subscribeToEvent('joined_room', confirmJoinCallback);

    return () => {
      // It's good practice to also emit a "leave_channel_room" if your backend supports it
      // For now, just unsubscribing from the confirmation.
      unsubscribeFromEvent('joined_room', confirmJoinCallback);
      if (socket) {
        socket.off('connect', handleConnect); // Clean up connect listener
      }
      // If you implement 'leave_channel_room', emit it here:
      // if (currentChannelId && socket && socket.connected) {
      //   emitEvent('leave_channel_room', { channelId: currentChannelId });
      // }
    };
  }, [activeContact]); // Re-run when activeContact (and thus channelId) changes

  // Effect to clear quote when active contact changes
  useEffect(() => {
    setQuotedMsgId(null);
    // console.log('Active contact changed, quote cleared.'); // Optional: for debugging
  }, [activeContact]);

  return (
    <div className="h-screen bg-gray-100 flex flex-col"> {/* Changed to flex-col for temp UI */}

      <div className="flex flex-1 overflow-hidden"> {/* Added wrapper for horizontal layout */}
        <ContactList
          contacts={contacts}
          activeContactId={activeContactId} // Pass activeContactId
          searchTerm={searchTerm}
          onContactSelect={selectContact}
          onSearchChange={updateSearchTerm}
          isLoading={isLoadingContacts}
          fetchNextPage={fetchNextPageContacts}
          hasNextPage={hasNextPageContacts}
          isFetchingNextPage={isFetchingNextPageContacts}
        />
      
        <ChatArea
          contact={activeContact || null} // activeContact can be null
          messages={messages}
          onSendMessage={sendMessage}
          isLoading={isLoadingMessages}
          fetchNextPage={fetchNextPageMessages}
          hasNextPage={hasNextPageMessages}
          isFetchingNextPage={isFetchingNextPageMessages}
          // Add new props for quoting
          quotedMsgId={quotedMsgId}
          setQuotedMsgId={setQuotedMsgId} // To allow MessageBubble to set it
        />
      </div>
    </div>
  );
};

export default ChatPage;
